"use client"

import AccessDenied from "@/components/custom-access-denied"
import { TAGS } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { t } from "@/languages/i18n"
import getDomains from "@/services/domain-services"
import { DomainEntity } from "@/types/domain"
import { PostData, TagAttributes } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import { SaveOutlined } from "@ant-design/icons"
import type { FormProps } from "antd"
import { Button, Form, Input, Select } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { fetchPostAll } from "../../post/services/post"
import { checkSlug, createTag, getTagById, updateTag } from "../services/tag"

interface TagFieldType {
    name: string
    slug: string
    description?: string
    domain?: string
    posts?: number[]
    locale: string
    status: "draft" | "published"
}

interface TagFormData {
    name: string
    slug: string
    description?: string
    domain?: string
    posts?: number[]
    locale: string
    publishedAt?: string | null
}

const sanitizeTagPayload = <T extends Record<string, any>>(payload: T): Partial<T> => {
    const sanitizedEntries = Object.entries(payload).filter(([, value]) => {
        if (value === null || value === undefined || value === "") return false
        if (Array.isArray(value) && value.length === 0) return false
        return true
    })

    return Object.fromEntries(sanitizedEntries) as Partial<T>
}

const localeOptions = [
    { value: "vi", label: t("Tiếng Việt") },
    { value: "en", label: t("Tiếng Anh") },
    { value: "cs", label: t("Tiếng Séc") },
]

const UpsertTag = () => {
    const [tag, setTag] = useState<TagAttributes | null>(null)
    const [loading, setLoading] = useState(false)
    const [websiteOptions, setWebsiteOptions] = useState<{ label: string; value: string }[]>([])
    const [postOptions, setPostOptions] = useState<{ label: string; value: number }[]>([])
    const [selectedLocale, setSelectedLocale] = useState("vi")
    const [form] = Form.useForm<TagFieldType>()
    const searchParams = useSearchParams()
    const id = searchParams.get("id")
    const isEditMode = !!id
    const { hasPermission: checkPermission } = useCheckPermission()
    const router = useRouter()
    const originalDataRef = useRef({ oldname: "", oldslug: "", publishedAt: null as string | null })
    const [isFormReady, setIsFormReady] = useState(false)

    const upsertTagQueryConfig = useMemo(
        () => ({
            fields: ["name", "slug", "description", "domain", "locale", "publishedAt"],
        }),
        [],
    )

    const getNormalizedSnapshot = useCallback(() => {
        const values = form.getFieldsValue(true);


        const normalized = normalizeFormData(values);


        return {
            ...normalized,

            posts: Array.isArray(normalized.posts) ? [...normalized.posts].sort() : null,


            locale: normalized.locale || "vi",
            status: normalized.status || "draft",
        };
    }, [form]);

    const { checkChange, commitSnapshot, message, handleCancel: guardCancel } = useGuardedForm({
        form,
        getSnapshot: getNormalizedSnapshot
    })

    if (!checkPermission("tag", isEditMode ? "update" : "create")) {
        return <AccessDenied />
    }

    const removeAccentsAndSpecialChars = (str: string) => {
        return str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[đĐ]/g, "d")
            .replace(/[,:"'<>?`!@#$%^&*();/\\]/g, "")
            .replace(/\s+/g, "-")
            .toLowerCase()
    }

    const fetchWebsiteOptions = async () => {
        try {
            const response = await getDomains()
            const domains = Array.isArray(response) ? (response as DomainEntity[]) : []
            setWebsiteOptions(
                domains.map(item => ({
                    label: item.attributes?.domain || item.attributes?.name || `#${item.id}`,
                    value: item.attributes?.domain || "",
                })),
            )
        } catch {
            message.error(t("Không thể tải danh sách website"))
        }
    }

    const fetchPostOptions = async (locale: string) => {
        try {
            const response = await fetchPostAll(
                {},
                locale,
                {
                    pageSize: "100",
                    publicationState: "preview",
                    fields: ["title", "slug"],
                    populate: null,
                    sort: "title:asc",
                },
            )
            const posts = Array.isArray(response?.data) ? (response.data as PostData[]) : []
            setPostOptions(
                posts.map(post => ({
                    label: post.attributes?.title || post.attributes?.slug || `#${post.id}`,
                    value: post.id,
                })),
            )
        } catch {
            message.error("Không thể tải danh sách bài viết")
        }
    }

    const handleNameBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        const nameValue = e.target.value.trim()
        if (!nameValue) {
            form.setFields([{ name: "slug", errors: [] }])
            return
        }

        const slugValue = removeAccentsAndSpecialChars(nameValue)

        const isSlugUnique = async (slug: string): Promise<boolean> => {
            try {
                const response: any = await checkSlug(slug, selectedLocale)
                const duplicatedItems = Array.isArray(response?.data) ? response.data : []
                if (isEditMode && tag?.slug === slug) {
                    return true
                }
                return duplicatedItems.length === 0
            } catch {
                return false
            }
        }

        const isUnique = await isSlugUnique(slugValue)
        form.setFieldsValue({ slug: slugValue })

        if (!isUnique) {
            form.setFields([{ name: "slug", errors: [t("Đường dẫn đã tồn tại. Vui lòng chọn đường dẫn khác.")] }])
            setTimeout(() => checkChange(), 0)
            return
        }

        form.setFields([{ name: "slug", errors: [] }])
        setTimeout(() => checkChange(), 0)
    }

    const onFinish: FormProps<TagFieldType>["onFinish"] = async values => {
        try {
            setLoading(true)
            let finalSlug = values.slug

            if (isEditMode) {
                const { oldname, oldslug } = originalDataRef.current
                if (values.name && values.name !== oldname && values.slug === oldslug) {
                    finalSlug = removeAccentsAndSpecialChars(values.name)
                }
            } else if (values.name && !values.slug) {
                finalSlug = removeAccentsAndSpecialChars(values.name)
            }

            const nextPublishedAt =
                values.status === "published"
                    ? originalDataRef.current.publishedAt || new Date().toISOString()
                    : null

            const tagData: TagFormData = {
                name: values.name,
                slug: finalSlug,
                description: values.description,
                domain: values.domain,
                posts: values.posts || [],
                locale: values.locale,
                publishedAt: nextPublishedAt,
            }
            const sanitizedTagData = sanitizeTagPayload(tagData)

            if (isEditMode) {
                await updateTag(id!, sanitizedTagData as TagAttributes)
                originalDataRef.current = {
                    oldname: values.name,
                    oldslug: finalSlug,
                    publishedAt: nextPublishedAt,
                }
                commitSnapshot()
                message.success(t("Thẻ đã được cập nhật thành công"))
            } else {
                await createTag(sanitizedTagData as TagAttributes)
                commitSnapshot()
                message.success(t("Thẻ đã được tạo thành công"))
                router.push(createPath(TAGS.LIST.PATH, searchParams))
            }
        } catch {
            message.error(isEditMode ? t("Cập nhật thẻ thất bại") : t("Tạo thẻ thất bại"))
        } finally {
            setLoading(false)
        }
    }

    const onFinishFailed: FormProps<TagFieldType>["onFinishFailed"] = () => { }

    useEffect(() => {
        fetchWebsiteOptions()
    }, [])

    useEffect(() => {
        fetchPostOptions(selectedLocale)
    }, [selectedLocale])

    useEffect(() => {
        if (isEditMode && id) {
            const fetchTag = async () => {
                try {
                    setLoading(true)
                    const response = await getTagById(Number(id), upsertTagQueryConfig)
                    setTag(response)

                    if (!response) {
                        message.error(t("Không tìm thấy thẻ"))
                        return
                    }

                    const tagData = response.attributes || response

                    if (!tagData || (!tagData.name && !tagData.slug)) {
                        message.error(t("Dữ liệu thẻ không hợp lệ"))
                        return
                    }

                    const locale = tagData.locale || "vi"
                    setSelectedLocale(locale)

                    const formData: TagFieldType = {
                        name: tagData.name || "",
                        slug: tagData.slug || "",
                        description: tagData.description || "",
                        domain: tagData.domain || undefined,
                        posts: tagData.posts?.data?.map((post: PostData) => post.id) || [],
                        locale,
                        status: tagData.publishedAt ? "published" : "draft",
                    }

                    form.setFieldsValue(formData)
                    originalDataRef.current = {
                        oldname: tagData.name || "",
                        oldslug: tagData.slug || "",
                        publishedAt: tagData.publishedAt || null,
                    }
                    setIsFormReady(true)
                } catch {
                    message.error(t("Lỗi khi tải dữ liệu thẻ"))
                } finally {
                    setLoading(false)
                }
            }

            fetchTag()
            return
        }

        form.setFieldValue("locale", selectedLocale)
        form.setFieldValue("status", "draft")
        setIsFormReady(true)
    }, [form, id, isEditMode, selectedLocale, upsertTagQueryConfig])

    useEffect(() => {
        if (isFormReady) {
            const animationId = requestAnimationFrame(() => {
                commitSnapshot();
            });
            return () => cancelAnimationFrame(animationId);
        }
    }, [isFormReady, commitSnapshot]);

    const initialValues = useMemo(
        () => ({
            locale: "vi",
            status: "draft" as const,
            posts: [] as number[],
        }),
        [],
    )

    const handleCancelClick = () => {
        const targetPath = createPath(TAGS.LIST.PATH, searchParams)
        guardCancel(targetPath)
    }

    if (isEditMode && !isFormReady) {
        return <FormSkeleton mainSections={2} sidebarCards={1} />
    }

    const crumbs = [
        { title: t("Thẻ"), href: createPath(TAGS.LIST.PATH, searchParams) },
        { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
    ]

    const main = (
        <>
            <FormSection title={t("Cơ bản")} collapsible={false}>
                <Form.Item
                    name="name"
                    label={<span className="text-base font-medium">{t("Tên")}</span>}
                    rules={[{ required: true, message: t("Tên là bắt buộc"), min: 1 }]}
                >
                    <Input onBlur={handleNameBlur} />
                </Form.Item>

                <Form.Item
                    name="slug"
                    label={<span className="text-base font-medium">{t("Đường dẫn")}</span>}
                    rules={[{ required: true, message: t("Đường dẫn là bắt buộc"), min: 1 }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item name="description" label={<span className="text-base font-medium">{t("Mô tả")}</span>}>
                    <Input.TextArea rows={4} />
                </Form.Item>

                <Form.Item
                    name="domain"
                    label={<span className="text-base font-medium">{t("Trang web")}</span>}
                    className="!mb-0"
                >
                    <Select
                        showSearch
                        options={websiteOptions}
                        placeholder={t("Chọn trang web")}
                        optionFilterProp="label"
                    />
                </Form.Item>
            </FormSection>
        </>
    )

    const sidebar = (
        <FormSection title={t("Xuất bản")} collapsible={false} compact>
            <Form.Item
                name="status"
                label={<span className="text-sm font-medium">{t("Trạng thái")}</span>}
                rules={[{ required: true, message: t("Vui lòng chọn trạng thái") }]}
            >
                <Select
                    options={[
                        { value: "draft", label: t("Bản nháp") },
                        { value: "published", label: t("Đã xuất bản") },
                    ]}
                    placeholder={t("Chọn trạng thái")}
                />
            </Form.Item>

            <Form.Item
                name="locale"
                label={<span className="text-sm font-medium">{t("Ngôn ngữ")}</span>}
                rules={[{ required: true, message: "Vui lòng chọn ngôn ngữ" }]}
                className="!mb-0"
            >
                <Select
                    options={localeOptions}
                    placeholder={t("Chọn ngôn ngữ")}
                    onChange={value => {
                        setSelectedLocale(value)
                        setTimeout(() => checkChange(), 0)
                    }}
                />
            </Form.Item>
        </FormSection>
    )

    const actionBar = (
        <StickyActionBar
            right={
                <>
                    <Button onClick={handleCancelClick}>{t("Hủy")}</Button>
                    <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={loading}>
                        {isEditMode ? t("Cập nhật thẻ") : t("Thêm thẻ mới")}
                    </Button>
                </>
            }
        />
    )

    return (
        <Form<TagFieldType>
            form={form}
            layout="vertical"
            initialValues={initialValues}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            onValuesChange={checkChange}
        >
            <FormShell
                title={isEditMode ? t("Chỉnh sửa thẻ") : t("Thêm thẻ mới")}
                crumbs={crumbs}
                main={main}
                sidebar={sidebar}
                actionBar={actionBar}
            />
        </Form>
    )
}

export default UpsertTag
